import axios from "axios";
import { API_URL } from "../constant";

const API_BASE_URL = process.env.REACT_APP_API_URL || API_URL;

// Create axios instance with auth headers
const api = axios.create({
  baseURL: API_BASE_URL,
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("auth_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const businessAgentAPI = {
  // Get all business agents (Super Admin only)
  getAllAgents: async (filters = {}) => {
    const params = new URLSearchParams();

    if (filters.per_page) params.append("per_page", filters.per_page);
    if (filters.page) params.append("page", filters.page);
    if (filters.status && filters.status !== "all")
      params.append("status", filters.status);
    if (filters.agent_type && filters.agent_type !== "all")
      params.append("agent_type", filters.agent_type);
    if (filters.search) params.append("search", filters.search);

    const response = await api.get(`/superadmin/business-agents?${params}`);
    return response.data;
  },

  // Get agent applications
  getAgentApplications: async (filters = {}) => {
    const params = new URLSearchParams();

    if (filters.per_page) params.append("per_page", filters.per_page);
    if (filters.page) params.append("page", filters.page);
    if (filters.status && filters.status !== "all")
      params.append("status", filters.status);
    if (filters.agent_type && filters.agent_type !== "all")
      params.append("agent_type", filters.agent_type);

    const response = await api.get(
      `/superadmin/business-agent-applications?${params}`
    );
    return response.data;
  },

  // Get specific agent application
  getAgentApplicationById: async (applicationId) => {
    const response = await api.get(
      `/superadmin/business-agent-applications/${applicationId}`
    );
    return response.data;
  },

  // Review agent application
  reviewAgentApplication: async (
    applicationId,
    action,
    agentType,
    commissionRate = null,
    rejectionReason = "",
    notes = ""
  ) => {
    const data = {
      action,
      agent_type: agentType,
      ...(commissionRate !== null && { commission_rate: commissionRate }),
      ...(rejectionReason && { rejection_reason: rejectionReason }),
      ...(notes && { notes }),
    };

    return api.post(
      `/superadmin/business-agent-applications/review/${applicationId}`,
      data
    );
  },

  // Edit agent application (for pending status)
  editAgentApplication: async (applicationId, agentType, notes = "") => {
    const data = {
      agent_type: agentType,
      notes,
    };

    return api.put(
      `/superadmin/business-agent-applications/${applicationId}/edit`,
      data
    );
  },

  // Update agent type (mini ↔ senior)
  updateAgentType: async (agentId, agentType, commissionRate, reason = "") => {
    const data = {
      agent_type: agentType,
      commission_rate: commissionRate,
      reason,
    };

    return api.put(`/superadmin/agents/${agentId}/update-type`, data);
  },

  // Get agent details
  getAgentDetails: async (agentId) => {
    const response = await api.get(`/superadmin/business-agents/${agentId}`);
    return response.data;
  },

  // Update agent status
  updateAgentStatus: async (
    agentId,
    isActive,
    commissionRate = null,
    reason = ""
  ) => {
    const data = {
      is_active: isActive,
      ...(reason && { reason }),
    };

    if (commissionRate !== null) {
      data.commission_rate = commissionRate;
    }

    const response = await api.put(
      `/superadmin/business-agents/${agentId}/status`,
      data
    );
    return response.data;
  },

  // Upgrade agent to senior
  upgradeToSeniorAgent: async (agentId, commissionRate = 15.0, reason = "") => {
    const response = await api.post(
      `/superadmin/business-agents/upgrade/${agentId}`,
      {
        commission_rate: commissionRate,
        ...(reason && { reason }),
      }
    );
    return response.data;
  },

  // Demote agent to mini
  demoteToMiniAgent: async (agentId, commissionRate = 10.0, reason = "") => {
    const response = await api.post(
      `/superadmin/business-agents/demote/${agentId}`,
      {
        commission_rate: commissionRate,
        reason,
      }
    );
    return response.data;
  },

  // Get mini agents
  getMiniAgents: async (filters = {}) => {
    const params = new URLSearchParams();

    if (filters.per_page) params.append("per_page", filters.per_page);
    if (filters.page) params.append("page", filters.page);
    if (filters.status && filters.status !== "all")
      params.append("status", filters.status);
    if (filters.search) params.append("search", filters.search);

    const response = await api.get(
      `/superadmin/mini-business-agents?${params}`
    );
    return response.data;
  },

  // Get senior agents
  getSeniorAgents: async (filters = {}) => {
    const params = new URLSearchParams();

    if (filters.per_page) params.append("per_page", filters.per_page);
    if (filters.page) params.append("page", filters.page);
    if (filters.status && filters.status !== "all")
      params.append("status", filters.status);
    if (filters.search) params.append("search", filters.search);

    const response = await api.get(
      `/superadmin/senior-business-agents?${params}`
    );
    return response.data;
  },

  // Get agent stats
  getAgentStats: async () => {
    const response = await api.get("/superadmin/business-agents/stats");
    return response.data;
  },

  // Get agent referral history
  getAgentReferralHistory: async (agentId, filters = {}) => {
    const params = new URLSearchParams();

    if (filters.per_page) params.append("per_page", filters.per_page);
    if (filters.page) params.append("page", filters.page);

    const response = await api.get(
      `/superadmin/business-agents/referral-history/${agentId}?${params}`
    );
    return response.data;
  },

  // Get agent transaction history (commissions)
  getAgentTransactionHistory: async (agentId, filters = {}) => {
    const params = new URLSearchParams();

    if (filters.per_page) params.append("per_page", filters.per_page);
    if (filters.page) params.append("page", filters.page);

    const response = await api.get(
      `/superadmin/advertising-agents/${agentId}/transaction-history?${params}`
    );
    return response.data;
  },

  // Get agent commission report
  getAgentCommissionReport: async (
    agentId,
    startDate = null,
    endDate = null
  ) => {
    const params = new URLSearchParams();

    if (startDate) params.append("start_date", startDate);
    if (endDate) params.append("end_date", endDate);

    const response = await api.get(
      `/superadmin/business-agents/${agentId}/commission-report?${params}`
    );
    return response.data;
  },

  // Update agent commission rate
  updateAgentCommissionRate: async (agentId, commissionRate, reason = "") => {
    const response = await api.put(
      `/superadmin/business-agents/${agentId}/commission-rate`,
      {
        commission_rate: commissionRate,
        reason,
      }
    );
    return response.data;
  },

  // Reactivate agent
  reactivateAgent: async (agentId, reason = "") => {
    const response = await api.post(
      `/superadmin/business-agents/${agentId}/reactivate`,
      {
        reason,
      }
    );
    return response.data;
  },

  // Get agent audit trail
  getAgentAuditTrail: async (agentId, filters = {}) => {
    const params = new URLSearchParams();

    if (filters.per_page) params.append("per_page", filters.per_page);
    if (filters.page) params.append("page", filters.page);
    if (filters.start_date) params.append("start_date", filters.start_date);
    if (filters.end_date) params.append("end_date", filters.end_date);

    const response = await api.get(
      `/superadmin/business-agent-audit/${agentId}?${params}`
    );
    return response.data;
  },

  // Get agent application logs
  getAgentApplicationLogs: async (applicationId = null, filters = {}) => {
    const params = new URLSearchParams();

    if (filters.per_page) params.append("per_page", filters.per_page);
    if (filters.page) params.append("page", filters.page);
    if (filters.action) params.append("action", filters.action);
    if (filters.start_date) params.append("start_date", filters.start_date);
    if (filters.end_date) params.append("end_date", filters.end_date);

    let url = "/superadmin/business-agent-logs";
    if (applicationId) {
      url = `/superadmin/business-agent-logs/${applicationId}`;
    }

    const response = await api.get(`${url}?${params}`);
    return response.data;
  },

  // Get user agent logs
  getUserAgentLogs: async (userId, filters = {}) => {
    const params = new URLSearchParams();

    if (filters.per_page) params.append("per_page", filters.per_page);
    if (filters.page) params.append("page", filters.page);
    if (filters.action) params.append("action", filters.action);
    if (filters.start_date) params.append("start_date", filters.start_date);
    if (filters.end_date) params.append("end_date", filters.end_date);
    if (filters.sort_by) params.append("sort_by", filters.sort_by);
    if (filters.sort_order) params.append("sort_order", filters.sort_order);

    const response = await api.get(
      `/superadmin/business-agent-user-logs/${userId}?${params}`
    );
    return response.data;
  },

  // Get batch users agent logs
  getBatchUsersAgentLogs: async (userIds, filters = {}) => {
    const response = await api.post(
      "/superadmin/business-agent-user-logs/batch",
      {
        user_ids: userIds,
        ...filters,
      }
    );
    return response.data;
  },

  // Get business agent count
  getBusinessAgentCount: async (filters = {}) => {
    try {
      const response = await api.get("/superadmin/count/retail-businesses", {
        params: filters,
        headers: {
          Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
        },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching business agent count:", error);
      throw error;
    }
  },

  // Export user agent logs
  exportUserAgentLogs: async (userId, filters = {}) => {
    const params = new URLSearchParams();

    if (filters.start_date) params.append("start_date", filters.start_date);
    if (filters.end_date) params.append("end_date", filters.end_date);
    if (filters.action) params.append("action", filters.action);

    const response = await api.get(
      `/superadmin/business-agent-logs/export/${userId}?${params}`,
      {
        responseType: "blob",
      }
    );
    return response;
  },

  // Debug agents
  debugAgents: async () => {
    const response = await api.get("/superadmin/debug-agents");
    return response.data;
  },
};

// Retail Business User Agent APIs (for retail business users who are agents)
export const retailBusinessAgentAPI = {
  // Apply to become a business agent
  applyForBusinessAgent: async (applicationData) => {
    const formData = new FormData();

    // Append all form data
    Object.keys(applicationData).forEach((key) => {
      if (key === "photo") {
        formData.append(key, applicationData[key]);
      } else {
        formData.append(key, applicationData[key]);
      }
    });

    const response = await api.post(
      "/retail-business/apply-business-agent",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response.data;
  },

  // Get my agent application
  getMyAgentApplication: async () => {
    const response = await api.get(
      "/retail-business/my-business-agent-application"
    );
    return response.data;
  },

  // Request agent upgrade
  requestAgentUpgrade: async (photo, reason = "") => {
    const formData = new FormData();
    formData.append("photo", photo);
    if (reason) {
      formData.append("reason", reason);
    }

    const response = await api.post(
      "/retail-business/request-business-agent-upgrade",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response.data;
  },

  // Get my agent referral history
  getMyAgentReferralHistory: async (filters = {}) => {
    const params = new URLSearchParams();

    if (filters.per_page) params.append("per_page", filters.per_page);
    if (filters.page) params.append("page", filters.page);

    const response = await api.get(
      `/retail-business/business-agent-referral-history?${params}`
    );
    return response.data;
  },

  // Get my agent transaction history
  getMyAgentTransactionHistory: async (filters = {}) => {
    const params = new URLSearchParams();

    if (filters.per_page) params.append("per_page", filters.per_page);
    if (filters.page) params.append("page", filters.page);

    const response = await api.get(
      `/retail-business/business-agent-transaction-history?${params}`
    );
    return response.data;
  },

  // Get detailed referral information with QR code
  getAgentReferralDetails: async () => {
    const response = await api.get(
      "/retail-business/business-agent-referral-details"
    );
    return response.data;
  },

  // Generate QR code
  generateReferralQRCode: async () => {
    const response = await api.get("/retail-business/generate-referral-qr");
    return response.data;
  },

  // Download QR code
  downloadReferralQRCode: async () => {
    const response = await api.get("/retail-business/download-referral-qr", {
      responseType: "blob",
    });
    return response;
  },

  // Get my agent wallet
  getMyAgentWallet: async () => {
    const response = await api.get("/retail-business/business-agent-wallet");
    return response.data;
  },

  // Get my agent history
  getMyAgentHistory: async (filters = {}) => {
    const params = new URLSearchParams();

    if (filters.per_page) params.append("per_page", filters.per_page);
    if (filters.page) params.append("page", filters.page);

    const response = await api.get(
      `/retail-business/business-agent-my-history?${params}`
    );
    return response.data;
  },

  updateAgentDetails: async (agentId, data) => {
    return api.put(`/superadmin/business-agents/${agentId}/details`, data);
  },

  createRetailer: async (formData) => {
  const response = await api.post('/retail-business/register', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
      'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
    }
  });
  return response.data;
}
};

export default api;
